import {sundayFirstToMondayFirst} from '../functions';

// Contract: {weekdays: [7 ints, index 0 = Monday ... 6 = Sunday], overrides: [{date: 'YYYY-MM-DD', min: int}]}
export const MAX_ATTENDANCE_MINIMUM = 1000;

export const DEFAULT_ATTENDANCE_MINIMUMS = Object.freeze({
    weekdays: Object.freeze([0, 0, 0, 0, 0, 0, 0]),
    overrides: Object.freeze([]),
});

export const dateKey = day => day.format('YYYY-MM-DD');

const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

const isValidMinimum = n => Number.isInteger(n) && n >= 0 && n <= MAX_ATTENDANCE_MINIMUM;

/**
 * A frozen copy of a payload that matches the contract, or null when it is malformed.
 * Callers must not substitute the default for null: that would hide stored data (and a later save would wipe it).
 */
export const normalizeAttendanceMinimums = data => {
    if (!data || !Array.isArray(data.weekdays) || data.weekdays.length !== 7 || !data.weekdays.every(isValidMinimum)) {
        return null;
    }
    if (!Array.isArray(data.overrides)) return null;
    const keys = new Set();
    for (const o of data.overrides) {
        if (!o || typeof o.date !== 'string' || !DATE_FORMAT.test(o.date) ||!isValidMinimum(o.min) || keys.has(o.date)) return null;
        keys.add(o.date);
    }
    return Object.freeze({
        weekdays: Object.freeze([...data.weekdays]),
        overrides: Object.freeze(data.overrides.map(({date, min}) => Object.freeze({date, min}))),
    });
};

/**
 * The minimum for a date: its override if there is one, else the weekday default.
 * @returns {{min: number, defaultMin: number, isOverride: boolean}}
 */
export const getEffectiveMinimum = (minimums, day) => {
    const {weekdays, overrides} = minimums || DEFAULT_ATTENDANCE_MINIMUMS;
    const defaultMin = weekdays[sundayFirstToMondayFirst(day.day())] || 0;
    const override = overrides.find(o => o.date === dateKey(day));
    return override
        ? {min: override.min, defaultMin, isOverride: true}
        : {min: defaultMin, defaultMin, isOverride: false};
};

export const isBelowMinimum = (available, min) => min > 0 && available < min;

export const withWeekdays = (minimums, weekdays) => ({...minimums, weekdays});

export const withOverride = (minimums, date, min) => ({
    ...minimums,
    overrides: [...minimums.overrides.filter(o => o.date !== date), {date, min}],
});

export const withoutOverride = (minimums, date) => ({
    ...minimums,
    overrides: minimums.overrides.filter(o => o.date !== date),
});

/**
 * Applies a save mutation to the latest minimums:
 * {kind: 'weekdays', weekdays} | {kind: 'override', date: 'YYYY-MM-DD', min} | {kind: 'resetOverride', date}
 * Returns the full object to PUT, or null for an unknown mutation.
 */
export const applyMinimumsMutation = (minimums, mutation) => {
    switch (mutation?.kind) {
        case 'weekdays':
            return withWeekdays(minimums, mutation.weekdays);
        case 'override':
            return withOverride(minimums, mutation.date, mutation.min);
        case 'resetOverride':
            return withoutOverride(minimums, mutation.date);
        default:
            return null;
    }
};

// Keeps digits only (no decimals, no negatives), drops leading zeros and caps at the API maximum.
export const sanitizeMinimumInput = text => {
    const digits = String(text ?? '').replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    if (!digits) return '';
    return String(Math.min(MAX_ATTENDANCE_MINIMUM, +digits));
};
